import { useState, useRef, useEffect, useImperativeHandle } from "react"
import { View, Pressable, Modal, TextInput as _TextInput } from "react-native"
import Icon from "@react-native-vector-icons/material-design-icons"
import { Show, useObservable } from "@legendapp/state/react"

import { ar } from "i18n/ar"

import { Scanner } from "./scanner"
import { ProductPickModal } from "./product-picker"
import { TextInput } from "./core"
import { trpc } from "@src/utils"
import { useCameraPermission } from "@src/hooks/use-camera-permission"

interface Props {
  withSearch?: boolean
  value?: string
  onValueChange: (upc: string, ref: React.RefObject<_TextInput | null>) => void
  autoFocus?: boolean
  className?: string
  skipFillingInput?: boolean
  skipFetching?: boolean
  ref?: React.Ref<{ focus: () => void }>
}

export function UpcInput({ value, onValueChange, autoFocus, withSearch, className, skipFillingInput, skipFetching, ref }: Props) {
  const showCamera$ = useObservable(false)
  const showSearchModal$ = useObservable(false)
  const { hasPermission, requestPermission } = useCameraPermission()
  const [barcode, setBarcode] = useState(value)
  const barcodeRef = useRef<_TextInput>(null)
  const utils = trpc.useUtils()

  // Expose focus method via ref prop (React 19 pattern)
  useImperativeHandle(ref, () => ({
    focus: () => barcodeRef.current?.focus(),
  }))

  function handleCameraSubmit(id: number) {
    const product = utils.products.get.getData({ id })
    if (!skipFillingInput) {
      setBarcode(product?.upc as string)
    }
    onValueChange(product?.upc as string, barcodeRef)
  }

  useEffect(() => {
    setBarcode(value)
  }, [value])

  const handleCameraPress = async () => {
    if (!hasPermission) return

    if (!hasPermission) {
      const granted = await requestPermission()
      if (!granted) return
    }

    barcodeRef.current?.focus()
    requestAnimationFrame(() => showCamera$.set(true))
  }

  function handleSearchPress() {
    showSearchModal$.set(true)
  }

  return (
    <>
      <View className={className}>
        <TextInput
          autoFocus={autoFocus}
          value={barcode}
          selectTextOnFocus
          onChangeText={setBarcode}
          onSubmitEditing={(ev) => ev.target.blur()}
          onBlur={() => {
            if (barcode && barcode.length >= 3) onValueChange(barcode, barcodeRef)
          }}
          placeholder={ar.enterOrScanBarcode}
          placeholderTextColor="#999999"
          ref={barcodeRef}
          returnKeyType="done"
          keyboardType="number-pad"
        />

        <View className="absolute end-0 top-0 bottom-0 flex-row gap-x-1 items-center">
          <Pressable onPress={handleCameraPress} className={`px-2 self-stretch justify-center ${withSearch ? "translate-x-[-8]" : ""}`}>
            <Icon name="qrcode-scan" size={22} color="#666" />
          </Pressable>

          {withSearch && (
            <Pressable onPress={handleSearchPress} className="px-2">
              <Icon name="magnify" size={24} color="#666" />
            </Pressable>
          )}
        </View>
      </View>

      <Show if={showCamera$}>
        <Modal animationType="slide" onRequestClose={() => showCamera$.set(false)}>
          <Scanner
            onSubmit={handleCameraSubmit}
            onSubmitUpc={(upc) => {
              onValueChange(upc, barcodeRef)
              requestAnimationFrame(() => {
                showCamera$.set(false)
              })
            }}
            hide={() => showCamera$.set(false)}
            skipFetching={skipFetching}
          />
        </Modal>
      </Show>

      <Show if={showSearchModal$}>
        <ProductPickModal
          mode="single"
          types={["LOCAL_PRODUCT", "STANDARD_PRODUCT", "WEIGHT_PRODUCT"]}
          value={[]}
          onChange={([selected]) => {
            const product = utils.products.get.getData({ id: selected })
            const upc = product?.upc

            if (!upc) {
              alert(ar.somethingWentWrong)
              return
            }

            if (!skipFillingInput) setBarcode(upc)
            onValueChange(upc, barcodeRef)
            showSearchModal$.set(false)
          }}
          hide={() => showSearchModal$.set(false)}
        />
      </Show>
    </>
  )
}
